import { useMemo } from "react";
import {
  Box,
  Autocomplete,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from "@financeable-com-au/financeable-ui";
import { INVOICE_BILLING_MONTHS, INVOICE_SECTIONS } from "@/constants";
import { InvoiceInfoProps } from "@/utils/interface";
import SectionCard from "./SectionCard";
import { TenantOption } from "../types";

const InvoiceInfo = ({
  value,
  tenants,
  loadingTenants,
  disabled,
  errors,
  expanded,
  onExpandedChange,
  onChange,
  onTenantSelect,
}: InvoiceInfoProps) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonthIndex = now.getMonth();
  const years = useMemo(() => Array.from({ length: 6 }, (_, i) => currentYear - 5 + i), [currentYear]);
  const availableMonths = useMemo(
    () =>
      value.billingYear >= currentYear ? INVOICE_BILLING_MONTHS.slice(0, currentMonthIndex) : INVOICE_BILLING_MONTHS,
    [value.billingYear, currentYear, currentMonthIndex],
  );
  const selectedTenant = tenants.find((t) => t.value === value.tenancyBillingConfigId) || null;

  const handleYearChange = (val: string) => {
    const newYear = Number(val);
    const monthIndex = value.billingMonth - 1;
    if (newYear >= currentYear && monthIndex >= currentMonthIndex) {
      const fallbackMonthIndex = Math.max(0, currentMonthIndex - 1);
      onChange({ billingYear: newYear, billingMonth: INVOICE_BILLING_MONTHS[fallbackMonthIndex].value });
    } else {
      onChange({ billingYear: newYear });
    }
  };

  return (
    <SectionCard
      title={INVOICE_SECTIONS.invoiceInfo}
      collapsible
      defaultExpanded
      disabled={disabled}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
    >
      <Stack direction="column" rowGap={3}>
        <FormControl error={!!errors?.invoiceTitle} required>
          <FormLabel>Invoice title</FormLabel>
          <Input
            placeholder="e.g. {Tenant name} - {Month} {Year} - Invoice"
            value={value.invoiceTitle}
            disabled={disabled}
            onChange={(e) => onChange({ invoiceTitle: e.target.value })}
          />
          {errors?.invoiceTitle && (
            <Typography level="body-xs" textColor="danger.500" style={{ marginTop: 4 }}>
              {errors.invoiceTitle}
            </Typography>
          )}
        </FormControl>

        <FormControl required>
          <FormLabel>Link to existing tenant</FormLabel>
          <Autocomplete
            placeholder="Select tenant"
            options={tenants}
            loading={loadingTenants}
            value={selectedTenant as unknown as object}
            error={!!errors?.tenancyBillingConfigId}
            disabled={disabled}
            isOptionEqualToValue={(opt, val) => (opt as TenantOption)?.value === (val as TenantOption)?.value}
            onChange={(_e, selection) => onTenantSelect((selection as TenantOption | null)?.value ?? null)}
          />
          <Typography level="body-xs" textColor="neutral.500" style={{ marginTop: 4 }}>
            Pick a tenant to auto-fill recipients, line items, and service usages instantly.
          </Typography>
        </FormControl>

        <Stack direction="row" columnGap={3}>
          <Box sx={{ flex: 1 }}>
            <FormControl>
              <FormLabel>Issue date</FormLabel>
              <Input
                type="date"
                value={value.issuedDate}
                disabled={disabled}
                onChange={(e) => onChange({ issuedDate: e.target.value })}
              />
            </FormControl>
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormControl>
              <FormLabel>Billing month</FormLabel>
              <Select
                value={String(value.billingMonth) as unknown as Record<string, unknown>}
                disabled={disabled}
                onChange={(_e, val) => val && onChange({ billingMonth: Number(val as unknown as string) })}
              >
                {availableMonths.map((m) => (
                  <Option key={m.value} value={String(m.value)}>
                    {m.label}
                  </Option>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormControl>
              <FormLabel>Billing year</FormLabel>
              <Select
                value={String(value.billingYear) as unknown as Record<string, unknown>}
                disabled={disabled}
                onChange={(_e, val) => val && handleYearChange(val as unknown as string)}
              >
                {years.map((y) => (
                  <Option key={y} value={String(y)}>
                    {y}
                  </Option>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </Stack>
    </SectionCard>
  );
};

export default InvoiceInfo;
